import React, { useMemo, useState } from "react";
import { ListCheck } from "lucide-react";
import useTripStore from "@/stores/tripBuilder/useTrip";
import { useBookingProgress } from "@/hooks/useBookingProgress";
import { CategoryKey } from "@/types/booking";
import CategoryCard from "./CategoryCard";

interface BookingProgressProps {
  locationId: string;
  variant?: "compact" | "expanded";
}

const BookingProgress: React.FC<BookingProgressProps> = ({
  locationId,
  variant,
}) => {
  const categoriesFromHook = useBookingProgress({ locationId });
  const progress = useMemo(() => {
    const totalCategories = categoriesFromHook.length;
    if (totalCategories === 0) return 0;

    const bookedCategories = categoriesFromHook.filter((category) => {
      return (
        category.items.length > 0 &&
        category.items.every((item) => item.bookingStatus === "booked")
      );
    }).length;

    return (bookedCategories / totalCategories) * 100;
  }, [categoriesFromHook]);

  return (
    <div
      className="w-full bg-white rounded-lg flex 2xl:flex-col p-2 2xl:px-4 2xl:py-3 transition-all duration-200"
      data-tutorial="overview-booking-progress"
      // onClick={() => setIsModalOpen(true)}
    >
      <div className="flex flex-col w-full">
        <div className="flex flex-row w-full items-center justify-between mb-1">
          <div className="flex flex-row space-x-2 items-center">
            <ListCheck className="w-4 h-4 text-neutral-800" />
            <p className="text-sm 2xl:text-[15px] text-neutral-400 font-light">
              Booking Progress
            </p>
          </div>
        </div>

        <div className="">
          <div className="h-1 2xl:w-full w-[97%] bg-neutral-100 rounded-full my-2">
            <div
              className="h-full bg-indigo-600 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="flex flex-row w-full gap-2 3xl:hidden">
            {categoriesFromHook.map((category) => {
              return (
                <CategoryCard
                  key={category.name}
                  config={category.config}
                  items={category.items}
                  variant="compact"
                />
              );
            })}
          </div>

          <div className="hidden 3xl:flex 3xl:mt-2 3xl:flex-col 3xl:space-y-1.5">
            {categoriesFromHook.map((category) => {
              const categoryKey = category.config.key;
              return (
                <CategoryCard
                  key={categoryKey}
                  config={category.config}
                  items={category.items}
                  variant="expanded"
                  // onClick={() => toggleCategory(categoryKey)}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="sm:max-w-[600px] 2xl:max-w-[700px] p-6"
        >
          <DialogHeader className="pb-4 border-b">
            <DialogTitle className="text-xl 2xl:text-2xl font-semibold text-neutral-900">
              {selectedLocation?.name} Booking Progress
            </DialogTitle>
            <DialogDescription className="text-xs 2xl:text-sm text-neutral-500">
              Track and manage your bookings for this location
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-[400px] 2xl:h-[450px] pr-3">
            <div className="space-y-3 py-4">
              {categoriesFromHook.map((category) => {
                const categoryKey = category.config.key;
                const items = category.items;
                const isExpanded = expandedCategories.includes(categoryKey);

                const isCompleted = items.every(
                  (item) => item.bookingStatus === "booked"
                );
                const isPending = items.some(
                  (item) => item.bookingStatus === "pending"
                );
                const statusText =
                  items.length === 0
                    ? "Not added"
                    : isCompleted
                    ? "Booked"
                    : isPending
                    ? "Pending"
                    : "Not booked";
                const statusBadgeText =
                  items.length === 0
                    ? "Not added"
                    : isCompleted
                    ? "Booked"
                    : isPending
                    ? "In progress"
                    : "Not booked";

                return (
                  <div
                    key={categoryKey}
                    className="bg-white rounded-lg p-2.5 2xl:p-3 border border-neutral-100 shadow-sm hover:border-indigo-100 transition-colors"
                  >
                    <button
                      onClick={() => toggleCategory(categoryKey)}
                      className="w-full flex items-start justify-between"
                    >
                      <div className="flex items-center space-x-3">
                        <div
                          className={cn(
                            "p-2 2xl:p-2.5 rounded-lg",
                            isCompleted
                              ? "bg-indigo-50 text-indigo-600"
                              : isPending
                              ? "bg-yellow-50 text-yellow-600"
                              : "bg-neutral-50 text-neutral-500"
                          )}
                        >
                          <i
                            className={cn(
                              "fa-solid",
                              category.config.icon,
                              "text-base"
                            )}
                          />
                        </div>
                        <div className="flex flex-col items-start space-y-1">
                          <h3 className="text-sm 2xl:text-base font-medium text-neutral-900">
                            {category.config.label}
                          </h3>
                          <p className="text-xs 2xl:text-sm font-light text-neutral-500">
                            {statusText} booked
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div
                          className={cn(
                            "flex items-center justify-center px-3 py-1 3xl:px-2 3xl:py-0.5 rounded-full text-xs 2xl:text-sm font-medium",
                            isCompleted
                              ? "bg-green-500/10 text-green-600"
                              : isPending
                              ? "bg-yellow-500/10 text-yellow-600"
                              : "bg-gray-500/10 text-gray-600"
                          )}
                        >
                          {statusBadgeText}
                        </div>
                        <motion.div
                          animate={{
                            rotate: isExpanded ? 180 : 0,
                          }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronDown className="w-4 h-4 2xl:w-5 2xl:h-5 text-neutral-500" />
                        </motion.div>
                      </div>
                    </button>

                    <motion.div
                      initial={false}
                      animate={{
                        height: isExpanded ? "auto" : 0,
                        opacity: isExpanded ? 1 : 0,
                      }}
                      transition={{
                        duration: 0.2,
                        ease: "easeInOut",
                      }}
                      className="overflow-hidden"
                    >
                      <div
                        className={cn(
                          "pt-2 2xl:pt-3",
                          isExpanded ? "opacity-100" : "opacity-0"
                        )}
                      >
                        {items.length > 0 ? (
                          <div className="space-y-1.5 2xl:space-y-2">
                            {items.map(
                              (item: BudgetBreakdownItem, index: number) => (
                                <motion.div
                                  key={`${item.id}-${item.bookingStatus}`}
                                  initial={{ opacity: 0, y: -10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{
                                    duration: 0.2,
                                    delay: index * 0.05,
                                  }}
                                  className="flex items-center justify-between py-2 px-3 bg-neutral-50 rounded-lg"
                                >
                                  <div className="flex items-center space-x-2">
                                    <div className="w-6 h-6 2xl:w-7 2xl:h-7 rounded-md overflow-hidden bg-white flex items-center justify-center">
                                      {item.image ? (
                                        <img
                                          src={item.image}
                                          alt={item.name}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        <FileImage className="w-3.5 h-3.5 2xl:w-4 2xl:h-4 text-neutral-400" />
                                      )}
                                    </div>
                                    <span className="text-xs 2xl:text-sm font-light text-neutral-700 max-w-[250px] truncate">
                                      {item.name}
                                    </span>
                                  </div>
                                  <button
                                    disabled={item.bookingStatus === "booked"}
                                    className={cn(
                                      "px-2 py-1 rounded-full text-xs 2xl:text-sm font-light flex items-center justify-center gap-2 transition-all duration-300 ease-in-out",
                                      item.bookingStatus === "booked"
                                        ? "bg-green-50 text-green-600 border border-green-500 cursor-not-allowed"
                                        : "bg-indigo-50 hover:bg-indigo-100 text-indigo-500 border border-indigo-500"
                                    )}
                                  >
                                    {item.bookingStatus === "booked" ? (
                                      <span className="flex items-center gap-1.5">
                                        Booked
                                      </span>
                                    ) : (
                                      <span className="transition-opacity duration-300 ease-in-out">
                                        Book Now
                                      </span>
                                    )}
                                  </button>
                                </motion.div>
                              )
                            )}
                          </div>
                        ) : (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="py-2 px-3 bg-neutral-50 rounded-lg"
                          >
                            <p className="text-xs 2xl:text-sm font-light text-neutral-500 text-center">
                              No {category.config.label.toLowerCase()} added yet
                            </p>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog> */}
    </div>
  );
};

export default BookingProgress;
